import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../common/icons";
import { UserContext } from "../../context/UserContext";
import { replayStudentTour } from "./replayStudentTour";
import annaLogo from "../../assets/Anna_1.png";
import { openGetAppCard, shouldOfferApp } from "../../pwa/installPrompt";
import UpdatesBell from "../studentUpdates/UpdatesBell";
import { requestSignOut } from "../chatbot/testShell/SignOutConfirm";
import "./StudentHeader.css";

// Shared by the student header (Navbar.js) and the landing page header (Header.js).

export const STUDENT_TABS = [
  { key: "results", label: "Results", to: "/result", icon: "bar-chart" },
  { key: "programs", label: "Programs", to: "/programs", icon: "graduation-cap" },
  { key: "appointments", label: "Appointments", to: "/appointments", icon: "calendar" },
  { key: "profile", label: "Profile", to: "/account", icon: "user" },
];

/** The tabs this student sees. Programs only when /auth/user/ says so: the career interests
 * test is assigned, they already have a career result, or they saved programs. */
export const tabsFor = (user) =>
  STUDENT_TABS.filter((tab) => tab.key !== "programs" || user?.shows_programs === true);

export const PHONE_QUERY = "(max-width: 767.98px)";

const usePhone = () => {
  const [phone, setPhone] = useState(() => window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const onChange = () => setPhone(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return phone;
};

export const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${parts[0][0]}${last}`.toUpperCase();
};

export const BrandLogo = () => (
  <Link to="/" className="sh-logo" aria-label="Anna home">
    <img src={annaLogo} alt="" />
    <span>Anna</span>
  </Link>
);

/**
 * The account button and its menu: a dropdown under the button, or a bottom sheet on phones.
 * `items` are links ({ key, label, icon, to, id }); Log out always comes last.
 */
export function AccountMenu({ open, onOpenChange, name, detail, items, onLogout }) {
  const phone = usePhone();
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // While open: focus the first item, close on Esc or a click outside.
  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.querySelector(".sh-menu-item")?.focus();

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      onOpenChange(false);
      buttonRef.current?.focus();
    };
    const onPointerDown = (event) => {
      const { target } = event;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      // The landing page tour drives this menu; clicks on the tour don't close it.
      if (target.closest?.(".gs-tour")) return;
      onOpenChange(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, onOpenChange]);

  const close = () => onOpenChange(false);

  const contents = (
    <>
      <div className="sh-menu-id">
        <span className="sh-avatar sh-avatar--lg" aria-hidden="true">{initials(name)}</span>
        <div>
          <div className="sh-menu-name">{name}</div>
          {detail && <div className="sh-menu-meta">{detail}</div>}
        </div>
      </div>
      <div className="sh-menu-sep" />
      {items.map((item) => (
        <Link key={item.key} id={item.id} className="sh-menu-item" to={item.to} onClick={close}>
          <Icon name={item.icon} size={20} />
          {item.label}
        </Link>
      ))}
      {/* Replays the landing page's quick tour (replayStudentTour.js) */}
      <button
        type="button"
        className="sh-menu-item"
        onClick={() => {
          close();
          replayStudentTour(navigate);
        }}
      >
        <Icon name="play-circle" size={20} />
        Quick tour
      </button>
      {shouldOfferApp() && (
        <button
          type="button"
          className="sh-menu-item"
          onClick={() => {
            close();
            openGetAppCard();
          }}
        >
          <Icon name="smartphone" size={20} />
          Get the app
        </button>
      )}
      <div className="sh-menu-sep" />
      {/* Asks first when test answers are still waiting on this device (F21) */}
      <button
        type="button"
        className="sh-menu-item is-danger"
        onClick={() => {
          close();
          requestSignOut(onLogout);
        }}
      >
        <Icon name="sign-out" size={20} />
        Log out
      </button>
    </>
  );

  return (
    <>
    {/* Updates bell (unread count + Updates sheet), students only */}
    <UpdatesBell />
    <div className="sh-account" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="sh-account-btn"
        aria-expanded={open}
        aria-label={`Account menu for ${name}`}
        onClick={() => onOpenChange(!open)}
      >
        <span className="sh-avatar" aria-hidden="true">{initials(name)}</span>
        <span className="sh-account-name">{name}</span>
        <Icon name="chevron-down" size={16} className="sh-chevron" />
      </button>
      {open && !phone && (
        <div className="sh-menu" ref={panelRef}>{contents}</div>
      )}
      {open && phone && createPortal(
        <>
          <div className="sh-scrim" aria-hidden="true" />
          <div className="sh-sheet" ref={panelRef} role="dialog" aria-label="Account">
            <div className="sh-sheet-handle" aria-hidden="true" />
            {contents}
          </div>
        </>,
        document.body,
      )}
    </div>
    </>
  );
}

/** The phone tab bar (hidden from 768 px up). `active` is "home" or a STUDENT_TABS key. */
export function StudentTabBar({ active }) {
  // StudentHeader.css leaves room for the bar at the end of the page while this class is set.
  useEffect(() => {
    document.body.classList.add("has-student-tabbar");
    return () => document.body.classList.remove("has-student-tabbar");
  }, []);

  const { user } = useContext(UserContext);
  const tabs = [{ key: "home", label: "Home", to: "/", icon: "home" }, ...tabsFor(user)];
  return (
    <nav className="sh-tabbar" aria-label="Student">
      {tabs.map((tab) => (
        <Link
          key={tab.key}
          to={tab.to}
          className="sh-tabbar-item"
          aria-current={active === tab.key ? "page" : undefined}
        >
          <span className="sh-tabbar-pill"><Icon name={tab.icon} size={22} /></span>
          <span className="sh-tabbar-label">{tab.label}</span>
        </Link>
      ))}
    </nav>
  );
}
